<template>
	<div v-if="svgContent" class="svg-icon" :style="svgStyle" v-html="svgContent" />
</template>

<script lang="ts">
// Truly module-level — runs once, shared across ALL Icon instances
const svgCache = new Map<string, string>()
const pendingFetches = new Map<string, Promise<string>>()

function getIconPath(name: string, prefix?: string): string {
	const normalized = prefix === 'country' ? name.toUpperCase() : name.toLowerCase()
	return prefix === 'country' ? `/icons/countries/${normalized}.svg` : `/icons/${normalized}.svg`
}

function fetchSvg(path: string): Promise<string> {
	if (svgCache.has(path)) return Promise.resolve(svgCache.get(path)!)
	if (pendingFetches.has(path)) return pendingFetches.get(path)!

	const promise = fetch(path)
		.then((res) => (res.ok ? res.text() : ''))
		.catch(() => '')
		.then((svg) => {
			svgCache.set(path, svg)
			pendingFetches.delete(path)
			return svg
		})

	pendingFetches.set(path, promise)
	return promise
}
</script>

<script setup lang="ts">
const props = defineProps<{
	height?: number
	width?: number | string
	name: string
	color?: string
	prefix?: string
}>()

const svgContent = ref('')

const svgStyle = computed(() => {
	const hasWidth = props.width !== undefined
	const hasHeight = props.height !== undefined

	if (!hasWidth && !hasHeight) return props.color ? { color: props.color } : undefined

	const w = hasWidth ? (typeof props.width === 'string' ? props.width : props.width + 'px') : props.height + 'px'
	const h = hasHeight ? props.height + 'px' : w

	return { width: w, minWidth: w, height: h, color: props.color, fontSize: h }
})

watch(
	() => props.name,
	async (name) => {
		if (!name) {
			svgContent.value = ''
			return
		}
		svgContent.value = await fetchSvg(getIconPath(name, props.prefix))
	},
	{ immediate: true },
)
</script>

<style lang="scss">
.svg-icon {
	width: var(--icon-size);
	min-width: var(--icon-size);
	height: var(--icon-size);
	display: inline-flex;

	svg {
		width: 100%;
		height: 100%;
		display: block;
	}
}
</style>
